/**
 * Die Punkte einer Aufgabe: abhaken, antippen zum Bearbeiten (20.10.2026), × zum Entfernen.
 * Aus `NotizKarte` gezogen, damit die Karte unter 300 Zeilen bleibt.
 */
import { useRef, useState, type MouseEvent as ReactMouseEvent } from "react";
import type { usePunktAendern, usePunktLoeschen } from "../../hooks/useNotizen";
import type { Notiz } from "../../lib/notizen";
import { MitBelegVerweisen } from "./BelegVerweise";

export default function NotizPunkte({ notiz, aendern, weg, aufText }: {
  notiz: Notiz;
  aendern: ReturnType<typeof usePunktAendern>;
  weg: ReturnType<typeof usePunktLoeschen>;
  aufText: (e: ReactMouseEvent) => boolean;
}) {
  const punktAendern = aendern;
  const punktWeg = weg;
  const [punktEdit, setPunktEditState] = useState<{ id: string; text: string } | null>(null);
  // Der Ref trägt den laufenden Stand: Enter und das folgende Blur (oder Escape und
  // Blur) dürfen nicht zweimal speichern bzw. beim Abbrechen doch speichern.
  const laufend = useRef<{ id: string; text: string } | null>(null);
  const setPunktEdit = (v: { id: string; text: string } | null) => { laufend.current = v; setPunktEditState(v); };
  const punktSpeichern = () => {
    const e = laufend.current;
    if (!e) return;
    const alt = notiz.punkte.find((x) => x.id === e.id)?.text ?? "";
    const neu = e.text.trim();
    // Leer heißt abbrechen, nicht löschen — dafür gibt es das ×.
    if (neu && neu !== alt) punktAendern.mutate({ id: e.id, text: neu });
    setPunktEdit(null);
  };
  if (notiz.punkte.length === 0) return null;
  return (
    <ul className="nz-punkte">
      {notiz.punkte.map((p) => (
        <li key={p.id} className={p.erledigt_am ? "is-erledigt" : ""}>
          <button
            type="button"
            className={"nz-haken" + (p.erledigt_am ? " is-an" : "")}
            aria-pressed={!!p.erledigt_am}
            aria-label={`„${p.text}“ ${p.erledigt_am ? "wieder öffnen" : "abhaken"}`}
            disabled={punktAendern.isPending}
            onClick={() => punktAendern.mutate({ id: p.id, erledigt: !p.erledigt_am })}
          />
          {punktEdit?.id === p.id ? (
            <input
              className="nz-feld nz-punkt-feld"
              value={punktEdit.text}
              maxLength={300}
              autoFocus
              aria-label="Punkt bearbeiten"
              onChange={(e) => setPunktEdit({ id: p.id, text: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); punktSpeichern(); }
                if (e.key === "Escape") setPunktEdit(null);
              }}
              onBlur={punktSpeichern}
            />
          ) : (
            <span className="nz-punkt-text nz-editierbar" title="Antippen zum Bearbeiten"
              onClick={(e) => { if (aufText(e)) setPunktEdit({ id: p.id, text: p.text }); }}>
              <MitBelegVerweisen text={p.text} />
            </span>
          )}
          <button type="button" className="nz-weg" aria-label={`„${p.text}“ entfernen`} onClick={() => punktWeg.mutate(p.id)}>×</button>
        </li>
      ))}
    </ul>
  );
}
